import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

export const sectionStyles: SxProps<Theme> = {
    mt: 3,
};

/**
 * The editor beside its preview on a desktop, stacked on anything narrower.
 *
 * The preview goes **under** the fields on a phone rather than above them, which is the
 * opposite of what a "preview" usually suggests. An admin on a phone is typing, and a preview
 * between the toolbar and the first field would push every field they came to edit off the
 * screen. It is still one scroll away, which is what it is worth on that width.
 */
export const layoutStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(min(420px, 100%), 1fr) minmax(min(360px, 100%), 1fr)' },
    gap: 3,
    alignItems: 'start',
};

export const cardStyles: SxProps<Theme> = {
    p: 3,
    mb: 3,
};

export const cardTitleStyles: SxProps<Theme> = {
    fontWeight: 600,
    mb: 0.5,
};

export const cardHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mb: 2.5,
};

/** Romanian beside English on a desktop; stacked below it, where two columns would be 160px each. */
export const bilingualRowStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 2,
    mb: 2,
};

export const cardRowStyles: SxProps<Theme> = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    p: 2,
    mb: 2,
};

export const cardRowHeaderStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 2,
};

export const cardRowTitleStyles: SxProps<Theme> = {
    fontWeight: 600,
    flexGrow: 1,
};

export const iconPickerStyles: SxProps<Theme> = {
    minWidth: 140,
};

export const iconOptionStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
};

export const addCardStyles: SxProps<Theme> = {
    mt: 1,
};

export const heroRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 2,
    mb: 2,
};

/** The stored photograph as it currently stands, at the aspect the band draws it. */
export const heroThumbStyles: SxProps<Theme> = {
    width: 200,
    height: 112,
    objectFit: 'cover',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
};

export const heroEmptyStyles: SxProps<Theme> = {
    width: 200,
    height: 112,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    px: 2,
    borderRadius: 2,
    border: '1px dashed',
    borderColor: 'divider',
    color: 'text.secondary',
};

export const actionsStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 2,
    flexWrap: 'wrap',
    alignItems: 'center',
};

/** The line beside a disabled Save that says why it is disabled. */
export const blockedSaveStyles: SxProps<Theme> = {
    color: 'error.main',
    alignSelf: 'center',
};

// --- The preview ----------------------------------------------------------

export const previewColumnStyles: SxProps<Theme> = {
    position: { xs: 'static', lg: 'sticky' },
    top: 16,
};

export const previewHeaderStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 1,
    mb: 1,
};

export const previewHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mb: 1.5,
};

/**
 * The window the real page is drawn inside.
 *
 * `overflow: hidden` plus a scaled child rather than an iframe: the page is a React component
 * and rendering it in place is the whole point -- an iframe would need a second React root and
 * a second copy of the theme, and the two could then disagree about the clinic's colours,
 * which is the one thing this preview must never do.
 */
export const previewFrameStyles: SxProps<Theme> = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: 'background.paper',
};

/**
 * The page at a third of its size, and inert.
 *
 * `pointer-events: none` is not decoration: the preview draws the page's real buttons, and a
 * click on one would navigate the admin away from the editor and lose everything they had
 * typed. `transform` rather than a media query, because the page is responsive -- shrinking
 * the *container* would give the admin the phone layout while their patients see the desktop
 * one, which is a preview of the wrong page.
 */
export const PREVIEW_SCALE = 0.34;

export const previewScalerStyles: SxProps<Theme> = {
    pointerEvents: 'none',
    userSelect: 'none',
    transform: `scale(${PREVIEW_SCALE})`,
    transformOrigin: 'top left',
    width: `${100 / PREVIEW_SCALE}%`,
    // The scaled page still occupies its unscaled height in the layout, so the wrapper's own
    // height has to be corrected by the same factor or the card ends three times too tall.
    marginBottom: `${(PREVIEW_SCALE - 1) * 100}%`,
};
